import { useState, MouseEvent as ReactMouseEvent } from 'react';
import clsx from 'clsx';

import { MODAL_ANIMATION_DURATION } from 'shared/config/constants';
import { useAppDispatch } from 'shared/lib/redux';
import { useAnimatedMounting, useEscapeKey } from 'shared/lib/utils';
import { Modal } from 'shared/ui/modal';
import { Tab, TabList } from 'shared/ui/tabs';
import { dateService } from 'shared/lib/date';
import { eventService, XApiEventKeys } from 'shared/api/event-service';

import { setIsLiteracyInsightModalVisible } from '../model';
import { LiteracyInsightModalInfo } from '../types';
import { patchHTMLWithLinks } from '../utils';
import { useLiteracyInsightLinksXApiEvents } from '../lib';

import { TeachingTip } from './teaching-tip';
import { InstructionalMaterials } from './instructional-materials';
import styles from './styles.module.scss';

export type LiteracyInsightModalProps = {
  literacyInsightModalInfo: LiteracyInsightModalInfo;
};

export const LiteracyInsightModal = ({
  literacyInsightModalInfo,
}: LiteracyInsightModalProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const dispatch = useAppDispatch();

  const { recommendations, sidebar, learnMoreLink, createdDate, insightId } =
    literacyInsightModalInfo;

  const handleCloseButtonXApiEvent = (isRightClick = false) => {
    eventService.send(XApiEventKeys.closedLiteracyInsightsModal, undefined, {
      result: {
        recommendationId: insightId,
        isDynamic: true,
        isRightClick,
      },
    });
  };

  const handleCloseButtonContextMenu = () => handleCloseButtonXApiEvent(true);

  const handleModalClose = () => {
    dispatch(setIsLiteracyInsightModalVisible(false));

    handleCloseButtonXApiEvent();
  };

  const { isMounted, handleAnimatedUnmount } = useAnimatedMounting(
    handleModalClose,
    MODAL_ANIMATION_DURATION,
  );
  useEscapeKey(handleModalClose);
  useLiteracyInsightLinksXApiEvents({
    insightId,
    isReady: isMounted,
    dependency: activeTabIndex,
  });

  const handleFirstOptionClick = (e: ReactMouseEvent) => {
    const isRightClick = e.type === 'contextmenu';

    if (!isRightClick) {
      setActiveTabIndex(0);
    }

    eventService.send(
      XApiEventKeys.selectedLiteracyInsightsModalOption,
      undefined,
      {
        result: {
          recommendationId: insightId,
          isDynamic: true,
          optionName: 'Teaching Tip of the Week',
          isRightClick,
        },
      },
    );
  };

  const handleSecondOptionClick = (e: ReactMouseEvent) => {
    const isRightClick = e.type === 'contextmenu';

    if (!isRightClick) {
      setActiveTabIndex(1);
    }

    eventService.send(
      XApiEventKeys.selectedLiteracyInsightsModalOption,
      undefined,
      {
        result: {
          recommendationId: insightId,
          isDynamic: true,
          optionName: 'Instructional Materials',
          isRightClick,
        },
      },
    );
  };

  return (
    <Modal
      accessibilityAttributes={{
        'aria-labelledby': 'literacy-insight-modal',
      }}
      isVisible={isMounted}
      modalClassnames={styles.modal}
      loopedTabulationSelectors="button, a"
    >
      <div
        className={styles.content}
        id="literacy-insight-modal"
        aria-label="Weekly Literacy Insight modal"
        data-testid="literacy-insight-modal"
      >
        <div className={styles.header}>
          <button
            onClick={handleAnimatedUnmount}
            onContextMenu={handleCloseButtonContextMenu}
            className={clsx('btn', 'btn-icon-ghost', styles.closeIconButton)}
            aria-label="Close Weekly Literacy Insight modal"
            data-testid="literacy-insight-modal-close-icon-btn"
          >
            <i className="icon-v2-close" aria-hidden />
          </button>
          <h3 className={styles.mainTitle}>Weekly Literacy Insight</h3>
          <div className={styles.dateContainer}>
            <i className="icon-v2-calendar" aria-hidden />
            <span className={styles.date}>
              {`Week of `}
              <time
                dateTime={dateService.getFormattedDate(
                  createdDate,
                  'YYYY-MM-DD hh:mm:sss',
                )}
              >
                {dateService.getFormattedDate(createdDate, 'MMM D, YYYY')}
              </time>
            </span>
          </div>
          <div
            className={styles.note}
            dangerouslySetInnerHTML={{ __html: sidebar }}
          />
        </div>
        {!!recommendations.length && (
          <div className={styles.body}>
            <div className={styles.optionContainer}>
              <TabList>
                <Tab
                  label="OPTION 1"
                  onClick={handleFirstOptionClick}
                  onContextMenu={handleFirstOptionClick}
                  index={0}
                  activeTabIndex={activeTabIndex}
                />
                <Tab
                  label="OPTION 2"
                  onClick={handleSecondOptionClick}
                  onContextMenu={handleSecondOptionClick}
                  index={1}
                  activeTabIndex={activeTabIndex}
                />
              </TabList>
              <TeachingTip
                index={0}
                activeTabIndex={activeTabIndex}
                recommendationOption={recommendations[0]}
              />
              <InstructionalMaterials
                index={1}
                activeTabIndex={activeTabIndex}
                recommendationOption={recommendations[1]}
              />
            </div>
          </div>
        )}
        <div className={styles.footer}>
          <p
            className={styles.learnMore}
            dangerouslySetInnerHTML={{
              __html: patchHTMLWithLinks(
                learnMoreLink,
                styles.learnMoreLink,
                styles.newTabIcon,
              ),
            }}
          />
          <button
            className={clsx(
              'btn btn-secondary btn-oval',
              styles.footerCloseButton,
            )}
            onClick={handleAnimatedUnmount}
            onContextMenu={handleCloseButtonContextMenu}
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};
